import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Flame, Sun, ArrowRight, ChevronRight } from 'lucide-react';
const pillars = [{
  num: '01',
  icon: Eye,
  title: 'Conștientizare',
  subtitle: 'Descoperă ce te ține pe loc',
  desc: 'Descoperi blocajele emoționale și credințele ascunse din subconștient care te împiedică să ai viața pe care ți-o dorești. Înveți să te observi fără judecată și să înțelegi de unde vin tiparele care se repetă.'
}, {
  num: '02',
  icon: Flame,
  title: 'Transformare',
  subtitle: 'Eliberează tiparele vechi',
  desc: 'Eliberezi credințele limitative și tiparele vechi care nu îți mai servesc. Prin meditații ghidate, tehnici practice și lucru cu subconștientul, înlocuiești ceea ce te blochează cu iubire de sine și încredere.'
}, {
  num: '03',
  icon: Sun,
  title: 'Abundență',
  subtitle: 'Activează viața pe care o dorești',
  desc: 'Activezi starea de abundență și integrezi schimbarea în viața de zi cu zi. Înveți să atragi mai multă prosperitate, relații armonioase și pace interioară, nu doar ca teorie, ci ca practică zilnică.'
}];
export function MethodSection() {
  return <section className="py-20 lg:py-32 bg-[#F8F3ED] relative overflow-hidden" data-id="element-1609">
      {/* Subtle background circles */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border-[1px] border-brand-purple/[0.06] rounded-full pointer-events-none" data-id="element-1610" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border-[1px] border-brand-purple/[0.08] rounded-full pointer-events-none" data-id="element-1611" />

      <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-1612">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16 lg:mb-20 max-w-3xl mx-auto" data-id="element-1613">
          <span className="font-body font-medium text-xs md:text-sm tracking-[0.2em] text-brand-purple uppercase mb-4 block" data-id="element-1614">
            Dezvoltare personală și spirituală
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-brand-text mb-6" data-id="element-1615">
            Procesul de Transformare în 3 Pași
          </h2>
          <div className="h-[2px] w-16 bg-brand-gold mx-auto rounded-full mb-8" data-id="element-1616" />
          <p className="font-subtitle text-lg md:text-xl text-brand-textSec leading-relaxed" data-id="element-1617">
            Calea prin care identifici ce te ține pe loc, eliberezi tiparele
            vechi și creezi o viață plină de abundență, echilibru și liniște
            interioară. Simplu, practic, pas cu pas.
          </p>
        </motion.div>

        {/* Framework Visual */}
        <div className="relative" data-id="element-1618">
          {/* Steps Grid */}
          <div className="grid sm:grid-cols-3 gap-6 sm:gap-0 items-stretch" data-id="element-1619">
            {pillars.map((pillar, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.7,
            delay: index * 0.25
          }} className="relative flex flex-col" data-id="element-1620">
                {/* Connector Arrow (between steps, tablet/desktop) */}
                {index < 2 && <div className="hidden sm:flex absolute top-1/2 -right-3 z-20 -translate-y-1/2" data-id="element-1621">
                    <div className="w-6 h-6 rounded-full bg-brand-purple/10 flex items-center justify-center" data-id="element-1622">
                      <ChevronRight className="w-4 h-4 text-brand-purple" strokeWidth={2} data-id="element-1623" />
                    </div>
                  </div>}

                {/* Mobile connector arrow */}
                {index < 2 && <div className="flex sm:hidden justify-center py-4" data-id="element-1624">
                    <div className="flex flex-col items-center gap-1" data-id="element-1625">
                      <div className="w-px h-6 bg-brand-purple/20" data-id="element-1626" />
                      <div className="w-6 h-6 rounded-full bg-brand-purple/10 flex items-center justify-center rotate-90" data-id="element-1627">
                        <ChevronRight className="w-4 h-4 text-brand-purple" strokeWidth={2} data-id="element-1628" />
                      </div>
                      <div className="w-px h-6 bg-brand-purple/20" data-id="element-1629" />
                    </div>
                  </div>}

                {/* Step Card */}
                <div className="relative rounded-2xl md:rounded-3xl p-5 sm:p-4 md:p-6 lg:p-8 bg-white border border-brand-purple/10 shadow-warm transition-all duration-500 hover:shadow-warm-hover hover:border-brand-gold/40 group mx-0 sm:mx-1 md:mx-2 lg:mx-3 h-full" data-id="element-1630">
                  {/* Large Background Number */}
                  <div className="absolute top-4 right-6 font-heading font-bold text-[80px] lg:text-[100px] text-brand-purple/[0.06] select-none leading-none transition-transform duration-500 group-hover:scale-110" data-id="element-1631">
                    {pillar.num}
                  </div>

                  {/* Step Label */}
                  <div className="flex items-center gap-3 mb-6 relative z-10" data-id="element-1632">
                    <div className="w-8 h-[2px] bg-brand-gold rounded-full" data-id="element-1633" />
                    <span className="font-body font-medium text-xs tracking-[0.15em] text-brand-purple uppercase" data-id="element-1634">
                      Pasul {pillar.num}
                    </span>
                  </div>

                  {/* Icon */}
                  <div className="w-14 h-14 rounded-xl bg-brand-purple/[0.08] border border-brand-purple/15 flex items-center justify-center mb-6 relative z-10 group-hover:bg-brand-gold/10 transition-colors duration-300" data-id="element-1635">
                    <pillar.icon className="w-7 h-7 text-brand-purple" strokeWidth={1.5} data-id="element-1636" />
                  </div>

                  {/* Title */}
                  <h3 className="font-heading font-bold text-2xl lg:text-3xl text-brand-text mb-2 relative z-10" data-id="element-1637">
                    {pillar.title}
                  </h3>

                  {/* Subtitle */}
                  <p className="font-subtitle text-brand-gold text-lg mb-4 relative z-10" data-id="element-1638">
                    {pillar.subtitle}
                  </p>

                  {/* Description */}
                  <p className="text-brand-textSec leading-relaxed text-lg relative z-10" data-id="element-1639">
                    {pillar.desc}
                  </p>

                  {/* Bottom accent bar */}
                  <div className="absolute bottom-0 left-6 right-6 h-[2px] rounded-full bg-gradient-to-r from-brand-gold/0 via-brand-gold to-brand-gold/0" style={{
                opacity: 0.2 + index * 0.2
              }} data-id="element-1640" />
                </div>
              </motion.div>)}
          </div>

          {/* Journey Summary Bar */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6,
          delay: 0.8
        }} className="mt-12 lg:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6" data-id="element-1641">
            <div className="flex items-center gap-3" data-id="element-1642">
              <div className="w-3 h-3 rounded-full border border-brand-textSec/30" data-id="element-1643" />
              <span className="text-brand-textSec font-body text-lg" data-id="element-1644">
                Blocaje
              </span>
            </div>
            <div className="hidden sm:block w-12 h-px bg-gradient-to-r from-brand-textSec/20 to-brand-gold/40" data-id="element-1645" />
            <div className="sm:hidden w-px h-6 bg-gradient-to-b from-brand-textSec/20 to-brand-gold/40" data-id="element-1646" />
            <div className="flex items-center gap-3" data-id="element-1647">
              <div className="w-3 h-3 rounded-full border border-brand-gold/40 bg-brand-gold/10" data-id="element-1648" />
              <span className="text-brand-textSec font-body text-lg" data-id="element-1649">
                Eliberare
              </span>
            </div>
            <div className="hidden sm:block w-12 h-px bg-gradient-to-r from-brand-gold/40 to-brand-gold/80" data-id="element-1650" />
            <div className="sm:hidden w-px h-6 bg-gradient-to-b from-brand-gold/40 to-brand-gold/80" data-id="element-1651" />
            <div className="flex items-center gap-3" data-id="element-1652">
              <div className="w-3 h-3 rounded-full bg-brand-gold" data-id="element-1653" />
              <span className="text-brand-gold font-body text-lg font-medium" data-id="element-1654">
                Abundență
              </span>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6,
          delay: 1
        }} className="mt-10 lg:mt-12 flex justify-center" data-id="element-1655">
            <a href="#programe" onClick={e => {
            e.preventDefault();
            const el = document.getElementById('programe');
            if (el) {
              el.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }} className="group inline-flex items-center justify-center gap-3 bg-brand-gold text-brand-cream font-heading font-semibold text-lg px-8 py-4 rounded-full hover:bg-brand-purple transition-all duration-300 shadow-warm hover:shadow-warm-hover hover:-translate-y-1 whitespace-nowrap" data-id="element-1656">
              Vreau să încep transformarea
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1657" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}